import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { allArticles } from "@/mocks/articles";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      {/* Gradient backdrop for hero visibility */}
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${
          isScrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
            : "bg-gradient-to-b from-black/50 via-black/30 to-transparent"
        }`}
      />

      <div className="relative w-full px-4 sm:px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Brand */}
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className={`font-heading text-lg sm:text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${
                isScrolled ? "text-foreground-900" : "text-background-50 drop-shadow-md"
              }`}
            >
              kakobuyspreadsheet<span className="text-accent-400">.ink</span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isScrolled ? "text-foreground-800 hover:text-primary-600" : "text-background-50/95 hover:text-accent-300 drop-shadow-sm"
              }`}
            >
              <i className="ri-home-4-line mr-1"></i>
              Home
            </Link>
            <a
              href="#categories"
              className={`text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isScrolled ? "text-foreground-800 hover:text-primary-600" : "text-background-50/95 hover:text-accent-300 drop-shadow-sm"
              }`}
            >
              Categories
            </a>
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-foreground-800 hover:text-primary-600" : "text-background-50/95 hover:text-accent-300 drop-shadow-sm"
                }`}
              >
                Browse All
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-background-50 border border-background-200/70 rounded-lg py-2 max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("guides");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className={`text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isScrolled ? "text-foreground-800 hover:text-primary-600" : "text-background-50/95 hover:text-accent-300 drop-shadow-sm"
              }`}
            >
              <i className="ri-book-open-line mr-1"></i>
              Shopping Guides
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold rounded-md bg-background-50 text-foreground-900 hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-sm"
            >
              Buy It Now
            </a>
            {/* Mobile menu toggle */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
            >
              <i
                className={`text-2xl transition-all duration-300 ${
                  isScrolled ? "text-foreground-800" : "text-background-50 drop-shadow-md"
                } ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Slide-in Panel */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-background-50 transition-transform duration-300 ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
              <span className="font-heading text-lg font-bold text-foreground-900">
                Menu
              </span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-600"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-1 mb-6">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Navigation</p>
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-home-4-line"></i>
                  </span>
                  Home
                </Link>
                <a
                  href="#categories"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-layout-grid-line"></i>
                  </span>
                  Categories
                </a>
                <a
                  href="#guides"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-book-open-line"></i>
                  </span>
                  Shopping Guides
                </a>
                <a
                  href="#benefits"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-star-line"></i>
                  </span>
                  Why Choose Us
                </a>
              </div>

              <div className="space-y-1 mb-6">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Product Categories</p>
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 flex-shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>

              <div className="space-y-1 mb-6">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Latest Guides</p>
                {allArticles.slice(0, 5).map((article) => (
                  <Link
                    key={article.id}
                    to={`/guide/${article.slug}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                      <i className="ri-article-line text-xs"></i>
                    </span>
                    <span className="truncate">{article.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 bg-primary-500 text-background-50 font-bold rounded-md hover:bg-primary-600 transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer text-sm"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}